import {IconCalendar, IconClock, IconShoppingCart} from "@tabler/icons-react";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {HeadingCarousel} from "@/components";
import {Carousel} from "@/components/Carousel";
import {useAuthStore} from "@/store/useAuth.store";
import useGetLotteryGames from "../../../../private/Lottery/hooks/useGetLotteryGames";
import {
  transformDrawsToSorteos,
  transformFiguresToAnimalData,
} from "../../../../private/Lottery/utils/lotteryDataTransformers";
import {getVenezuelanTime, isDrawPassed} from "../../../../private/Lottery/utils/timeUtils";
import styles from "./AnimalitosExpressCarousel.module.css";
import QuickBuyModal from "./QuickBuyModal";

const ANIMALITOS_GAME_TYPES = ["Animalitos", "animals"];
const PILL_ALL = "todas";
const MIXED_PER_LOTTERY = 3;
const MIXED_TOTAL_CAP = 18;
const SINGLE_LOTTERY_CAP = 20;
const MIN_CARDS_DESIRED = 8;
const LOCAL_ICON_PATH = "/src/assets/images/lottery-icons";

const getImageSrc = animal => animal.imageUrl || `${LOCAL_ICON_PATH}/${animal.name}.svg`;

const seededShuffle = arr => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const getVenezuelanDateStr = () => {
  const now = getVenezuelanTime();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}`;
};

const getValidSorteos = draws => {
  const sorteos = transformDrawsToSorteos(draws || []);
  return sorteos.filter(s => !isDrawPassed(s.hora, s.closeTime));
};

const buildAnimalitosGames = rawGames => {
  if (!Array.isArray(rawGames)) return [];
  return rawGames.filter(
    g => ANIMALITOS_GAME_TYPES.includes(g.game_type) && g.status === "active" && g.figures?.length
  );
};

const buildCardsForGame = game => {
  const animals = transformFiguresToAnimalData(game.figures);
  const validSorteos = getValidSorteos(game.draws);
  if (validSorteos.length === 0) return [];

  const nextSorteo = validSorteos[0];
  const dateStr = getVenezuelanDateStr();

  return animals.map(animal => ({
    key: `${game.id}-${nextSorteo.id}-${animal.number}`,
    gameId: game.id,
    gameName: game.name,
    drawTime: nextSorteo.hora,
    drawName: nextSorteo.nombre,
    drawDate: dateStr,
    drawId: nextSorteo.id,
    number: animal.number,
    displayName: animal.displayName,
    name: animal.name,
    imageUrl: getImageSrc(animal),
  }));
};

const buildMixedCards = games => {
  const perLottery = [];
  for (const game of games) {
    const all = buildCardsForGame(game);
    if (all.length === 0) continue;
    const shuffled = seededShuffle(all);
    perLottery.push(shuffled.slice(0, MIXED_PER_LOTTERY));
  }
  let mixed = seededShuffle(perLottery.flat());

  if (mixed.length < MIN_CARDS_DESIRED) {
    const usedKeys = new Set(mixed.map(c => c.key));
    for (const game of games) {
      const all = buildCardsForGame(game);
      const extra = all.filter(c => !usedKeys.has(c.key));
      const shuffled = seededShuffle(extra);
      for (const card of shuffled) {
        if (mixed.length >= MIXED_TOTAL_CAP) break;
        mixed.push(card);
        usedKeys.add(card.key);
      }
      if (mixed.length >= MIN_CARDS_DESIRED) break;
    }
    mixed = seededShuffle(mixed);
  }

  return mixed.slice(0, MIXED_TOTAL_CAP);
};

const buildSingleLotteryCards = game => buildCardsForGame(game).slice(0, SINGLE_LOTTERY_CAP);

const SkeletonCard = ({idx}) => (
  <div
    className={styles.skeleton}
    key={`skel-${idx}`}
  >
    <div className={styles.skeletonImage} />
    <div className={styles.skeletonLine} />
    <div className={styles.skeletonLine} />
    <div className={styles.skeletonLine} />
    <div className={styles.skeletonBtn} />
  </div>
);

const AnimalitosExpressCarousel = () => {
  const {t} = useTranslation();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const [activePill, setActivePill] = useState(PILL_ALL);
  const [modalCard, setModalCard] = useState(null);
  const [carouselApi, setCarouselApi] = useState(null);

  const {data: lotteryGamesResponse, isLoading, isFetching} = useGetLotteryGames();
  const showSkeletons = isLoading || isFetching;
  const isUnauthenticated = authStatus === "unauthenticated";

  const animalitosGames = useMemo(() => buildAnimalitosGames(lotteryGamesResponse?.data?.data), [lotteryGamesResponse]);

  const gamesById = useMemo(() => {
    const map = {};
    for (const g of animalitosGames) map[g.id] = g;
    return map;
  }, [animalitosGames]);

  const pills = useMemo(() => {
    const items = [{id: PILL_ALL, label: t("animalitosExpressAll", "Todas")}];
    for (const game of animalitosGames) {
      const hasValid = getValidSorteos(game.draws).length > 0;
      if (hasValid) items.push({id: String(game.id), label: game.name});
    }
    return items;
  }, [animalitosGames, t]);

  const cards = useMemo(() => {
    if (animalitosGames.length === 0) return [];
    if (activePill === PILL_ALL) return buildMixedCards(animalitosGames);
    const selected = animalitosGames.find(g => String(g.id) === activePill);
    return selected ? buildSingleLotteryCards(selected) : [];
  }, [animalitosGames, activePill]);

  const handleBuy = useCallback(
    card => {
      if (authStatus === "authenticated") {
        setModalCard(card);
      } else {
        handleShowAuthModal();
      }
    },
    [authStatus, handleShowAuthModal]
  );

  const handlePillClick = useCallback(id => setActivePill(id), []);

  if (!showSkeletons && !isUnauthenticated && animalitosGames.length === 0) return null;

  return (
    <div className={styles.container}>
      <HeadingCarousel
        title={t("animalitosExpressTitle")}
        subtitle={t("animalitosExpressSubTitle")}
        icon="🐾"
        viewAllLink="/lottery"
        onPrev={carouselApi?.scrollPrev}
        onNext={carouselApi?.scrollNext}
      />

      {!showSkeletons && pills.length > 1 && (
        <div className={styles.pillsRow}>
          {pills.map(pill => (
            <button
              key={pill.id}
              type="button"
              className={`${styles.pill} ${activePill === pill.id ? styles.pillActive : ""}`}
              onClick={() => handlePillClick(pill.id)}
            >
              {pill.label}
            </button>
          ))}
        </div>
      )}

      <div>
        <Carousel
          onInit={setCarouselApi}
          speed={1}
          key={activePill}
          renderItems={() =>
            showSkeletons || isUnauthenticated
              ? Array.from({length: 6}, (_, i) => (
                  <SkeletonCard
                    idx={i}
                    // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders
                    key={`skel-${i}`}
                  />
                ))
              : cards.map(card => (
                  <div
                    key={card.key}
                    className={styles.card}
                    onClick={() => handleBuy(card)}
                  >
                    <div className={styles.imageWrap}>
                      <div className={styles.accentBar} />
                      <span className={styles.numberBadge}>{String(card.number).padStart(2, "0")}</span>
                      <img
                        src={card.imageUrl}
                        alt={card.displayName}
                        className={styles.animalImage}
                        loading="lazy"
                        onError={e => {
                          e.target.onerror = null;
                          e.target.src = `${LOCAL_ICON_PATH}/${card.name}.svg`;
                        }}
                      />
                    </div>

                    <div className={styles.info}>
                      <span className={styles.animalName}>{card.displayName}</span>
                      <span className={styles.lotteryName}>{card.gameName}</span>
                      <div className={styles.drawMeta}>
                        {card.drawDate && (
                          <span className={styles.drawDate}>
                            <IconCalendar
                              size={10}
                              stroke={2}
                            />
                            {card.drawDate}
                          </span>
                        )}
                        {card.drawTime && (
                          <span className={styles.drawTime}>
                            <IconClock
                              size={10}
                              stroke={2}
                            />
                            {card.drawTime}
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      className={styles.buyBtn}
                      onClick={e => {
                        e.stopPropagation();
                        handleBuy(card);
                      }}
                    >
                      <IconShoppingCart
                        size={13}
                        stroke={2.5}
                      />
                      {t("animalitosExpressBuy")}
                    </button>
                  </div>
                ))
          }
        />
      </div>

      {modalCard && (
        <QuickBuyModal
          card={modalCard}
          game={gamesById[modalCard.gameId]}
          onClose={() => setModalCard(null)}
        />
      )}
    </div>
  );
};

export default AnimalitosExpressCarousel;
